//! Pixi layer that renders Excel-native charts from core's display lists.
//!
//! Charts are floating objects positioned by pixel rect on the sheet (core
//! computes the rect from the chart's cell anchor). The layer receives full
//! per-sheet chart lists via the `sheetCharts` event, draws each display list
//! into its own container, culls against the viewport, and draws the
//! selection outline + resize handles for the selected chart.

import { events, type DirtyObject } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import { chartAnchorRect } from '@/app/gridGL/charts/chartAnchorRect';
import { hitsShape } from '@/app/gridGL/charts/chartHitRegions';
import { drawDisplayList } from '@/app/gridGL/charts/drawDisplayList';
import { intersectsFrozenBand } from '@/app/gridGL/helpers/frozenCull';
import { intersects } from '@/app/gridGL/helpers/intersects';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { isMobileMode } from '@/app/helpers/isMobile';
import type { CellRefRange, ChartHitRegion, JsCellsAccessed, JsChart, JsChartLayout } from '@/app/quadratic-core-types';
import { multiplayer } from '@/app/web-workers/multiplayerWebWorker/multiplayer';
import { Container, Graphics, Rectangle, type Point } from 'pixi.js';

export const CHART_RESIZE_HANDLE_SIZE = 8;

// hit-test tolerance multiplier on touch devices (drawn handles stay small,
// but fingers need a bigger target than a mouse cursor)
const TOUCH_HANDLE_TOLERANCE_MULTIPLIER = 3;

interface ChartEntry {
  container: Container;
  data: JsChart;
  bounds: Rectangle;
  /// the size the container's current contents were laid out for (differs
  /// from data.w/h while a resize-preview display list is showing)
  layoutSize: { w: number; h: number };
  /// hover hit regions matching the container's current contents
  hitRegions: ChartHitRegion[];
}

/** Which part of a chart a pointer position hits. */
export interface ChartHit {
  chart: JsChart;
  /** Resize edge/corner: combination of 'n'/'s' + 'e'/'w', or 'move'. */
  handle: 'move' | 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
}

/** A hit chart data-point region, with its tooltip text. */
export interface ChartRegionHit {
  chartId: string;
  region: ChartHitRegion;
  title: string;
  value?: string;
}

export class ChartsLayer extends Container {
  // keyed by sheetId, then chart id
  private chartsPerSheet: Map<string, Map<string, ChartEntry>> = new Map();

  private selection: Graphics;

  // chart element highlighted by clicking a data point; its source ranges
  // are simultaneously highlighted on the grid via content.cellHighlights
  private highlightedRegion?: { chartId: string; region: ChartHitRegion };

  // live preview of the chart editor's in-progress edits: existing charts
  // preview via their own entry (transient ops); new charts draw as a ghost
  private editPreviewId?: string;
  private editPreviewGhost?: { container: Container; bounds: Rectangle };

  dirty = false;

  constructor() {
    super();
    this.selection = new Graphics();
    events.on('sheetCharts', this.handleSheetCharts);
    events.on('changeSheet', this.setDirty);
    events.on('sheetOffsetsUpdated', this.repositionCharts);
    // reposition live while a column/row resize is being dragged (the
    // transient offsets only exist client-side until the resize commits)
    events.on('resizeHeadingColumn', this.repositionCharts);
    events.on('resizeHeadingRow', this.repositionCharts);
    // chart selection lives in the sheet cursor; redraw whenever it changes
    events.on('cursorPosition', this.handleCursorPosition);
    // other players' chart selections are drawn by this layer
    events.on('multiplayerCursor', this.setDirty);
    events.on('setDirty', this.handleSetDirty);
  }

  destroy() {
    events.off('sheetCharts', this.handleSheetCharts);
    events.off('changeSheet', this.setDirty);
    events.off('sheetOffsetsUpdated', this.repositionCharts);
    events.off('resizeHeadingColumn', this.repositionCharts);
    events.off('resizeHeadingRow', this.repositionCharts);
    events.off('cursorPosition', this.handleCursorPosition);
    events.off('multiplayerCursor', this.setDirty);
    events.off('setDirty', this.handleSetDirty);
    super.destroy({ children: true });
  }

  private handleSetDirty = (dirty: DirtyObject) => {
    if (dirty.multiplayerCursor) {
      this.dirty = true;
    }
  };

  private handleCursorPosition = () => {
    this.dirty = true;
    // moving the selection off a chart drops its element highlight
    if (this.highlightedRegion && this.highlightedRegion.chartId !== this.selectedId) {
      this.clearRegionHighlight();
    }
  };

  private setDirty = () => {
    this.dirty = true;
  };

  /**
   * Recomputes every chart's pixel rect from its cell anchor against the
   * sheet's current offsets. Charts whose size changed (two-cell anchors)
   * show a stretch preview; the reflowed display list arrives from core via
   * `sheetCharts` once the resize commits.
   */
  private repositionCharts = (sheetId: string) => {
    const sheetCharts = this.chartsPerSheet.get(sheetId);
    if (!sheetCharts?.size) return;
    const sheet = sheets.getById(sheetId);
    if (!sheet) return;
    for (const entry of sheetCharts.values()) {
      const rect = chartAnchorRect(entry.data, sheet);
      entry.bounds = rect;
      entry.container.position.set(rect.x, rect.y);
      if (entry.layoutSize.w > 0 && entry.layoutSize.h > 0) {
        entry.container.scale.set(rect.width / entry.layoutSize.w, rect.height / entry.layoutSize.h);
      }
    }
    this.dirty = true;
    if (sheetId === sheets.current) {
      events.emit('setDirty', { cursor: true });
    }
  };

  /** Draws an entry's authoritative display list, redrawing once any
   * picture-fill images finish decoding. */
  private drawEntry(entry: ChartEntry) {
    drawDisplayList(entry.container, entry.data.ops, entry.data.images, () => {
      this.drawEntry(entry);
      this.dirty = true;
      events.emit('setDirty', { cursor: true });
    });
  }

  private handleSheetCharts = (sheetId: string, charts: JsChart[]) => {
    let sheetCharts = this.chartsPerSheet.get(sheetId);
    if (!sheetCharts) {
      sheetCharts = new Map();
      this.chartsPerSheet.set(sheetId, sheetCharts);
    }

    // remove charts that no longer exist
    const ids = new Set(charts.map((c) => c.id));
    for (const [id, entry] of sheetCharts) {
      if (!ids.has(id)) {
        entry.container.destroy({ children: true });
        sheetCharts.delete(id);
        if (sheets.getById(sheetId)?.cursor.selectedChartId === id) {
          sheets.getById(sheetId)?.cursor.selectChart(undefined);
        }
        if (this.highlightedRegion?.chartId === id) {
          this.clearRegionHighlight();
        }
      }
    }

    // add or update charts
    for (const data of charts) {
      let entry = sheetCharts.get(data.id);
      if (!entry) {
        entry = {
          container: this.addChild(new Container()),
          data,
          bounds: new Rectangle(data.x, data.y, data.w, data.h),
          layoutSize: { w: data.w, h: data.h },
          hitRegions: data.hit_regions ?? [],
        };
        sheetCharts.set(data.id, entry);
      } else {
        entry.data = data;
        entry.bounds = new Rectangle(data.x, data.y, data.w, data.h);
        entry.layoutSize = { w: data.w, h: data.h };
        entry.hitRegions = data.hit_regions ?? [];
      }
      entry.container.position.set(data.x, data.y);
      // clear any transient drag/resize scale: the ops are re-laid-out for
      // the new size, so the content reflows rather than stretches
      entry.container.scale.set(1, 1);
      this.drawEntry(entry);

      // the chart's data changed, so a highlighted element's geometry may
      // no longer match
      if (this.highlightedRegion?.chartId === data.id) {
        this.clearRegionHighlight();
      }
    }

    // keep the selection graphics on top
    this.addChild(this.selection);
    this.dirty = true;
    if (sheetId === sheets.current) {
      events.emit('setDirty', { cursor: true, multiplayerCursor: true });
    }
  };

  /** Whether any sheet has native charts. */
  hasCharts(): boolean {
    for (const sheetCharts of this.chartsPerSheet.values()) {
      if (sheetCharts.size > 0) return true;
    }
    return false;
  }

  /** Finds a chart by id on any sheet. */
  getChart(id: string): JsChart | undefined {
    for (const sheetCharts of this.chartsPerSheet.values()) {
      const entry = sheetCharts.get(id);
      if (entry) return entry.data;
    }
    return undefined;
  }

  /** All charts on the given sheet. */
  sheetCharts(sheetId: string): JsChart[] {
    const sheetCharts = this.chartsPerSheet.get(sheetId);
    if (!sheetCharts) return [];
    return [...sheetCharts.values()].map((entry) => entry.data);
  }

  /** All charts on the current sheet. */
  currentSheetCharts(): JsChart[] {
    return this.sheetCharts(sheets.current);
  }

  /** The selected chart's id on the current sheet (from the sheet cursor). */
  private get selectedId(): string | undefined {
    return sheets.sheet.cursor.selectedChartId;
  }

  /** The currently selected chart, if any. */
  get selected(): JsChart | undefined {
    const selectedId = this.selectedId;
    if (!selectedId) return undefined;
    return this.chartsPerSheet.get(sheets.current)?.get(selectedId)?.data;
  }

  /** Selects a chart (or deselects with undefined) via the sheet cursor, the
   * single source of truth for the selection (name box, multiplayer, ...). */
  select(id: string | undefined) {
    if (this.selectedId === id) return;
    sheets.sheet.cursor.selectChart(id);
    // moving the selection off a chart drops its element highlight
    if (this.highlightedRegion && this.highlightedRegion.chartId !== id) {
      this.clearRegionHighlight();
    }
    this.dirty = true;
  }

  /** Whether a clicked chart element is currently highlighted. */
  get hasRegionHighlight(): boolean {
    return this.highlightedRegion !== undefined;
  }

  /**
   * Highlights a clicked chart element (accent outline over the bar, slice,
   * or point) and its source data ranges on the grid.
   */
  highlightRegion(hit: ChartRegionHit) {
    this.highlightedRegion = { chartId: hit.chartId, region: hit.region };
    this.highlightSourceCells(hit.region.source_ranges ?? []);
    this.dirty = true;
  }

  /** Clears the clicked-element highlight and its grid source highlight. */
  clearRegionHighlight() {
    if (!this.highlightedRegion) return;
    this.highlightedRegion = undefined;
    content.cellHighlights.clear();
    this.dirty = true;
  }

  /** Highlights the element's source A1 ranges with the formula-style
   * colored dashed outlines. */
  private highlightSourceCells(ranges: string[]) {
    const cellsAccessed: JsCellsAccessed[] = [];
    for (const range of ranges) {
      try {
        const selection = sheets.stringToSelection(range, sheets.current);
        cellsAccessed.push({
          sheetId: selection.getSheetId(),
          ranges: JSON.parse(selection.getRanges()) as CellRefRange[],
        });
      } catch {
        // an unresolvable range (e.g. a reference to a sheet that wasn't
        // imported) simply isn't highlighted
      }
    }
    content.cellHighlights.fromCellsAccessed(cellsAccessed.length ? cellsAccessed : null, false);
  }

  /**
   * Hit tests a world position against charts on the current sheet. When the
   * chart is selected, edges/corners hit resize handles; anywhere inside a
   * chart hits 'move'.
   */
  hitTest(world: Point): ChartHit | undefined {
    const sheetCharts = this.chartsPerSheet.get(sheets.current);
    if (!sheetCharts) return undefined;

    const tolerance =
      (CHART_RESIZE_HANDLE_SIZE * (isMobileMode() ? TOUCH_HANDLE_TOLERANCE_MULTIPLIER : 1)) / pixiApp.viewport.scaled;

    // iterate in reverse so charts drawn on top hit first
    const entries = [...sheetCharts.values()].reverse();
    for (const entry of entries) {
      const b = entry.bounds;
      const isSelected = entry.data.id === this.selectedId;

      if (isSelected) {
        const nearLeft = Math.abs(world.x - b.x) <= tolerance;
        const nearRight = Math.abs(world.x - (b.x + b.width)) <= tolerance;
        const nearTop = Math.abs(world.y - b.y) <= tolerance;
        const nearBottom = Math.abs(world.y - (b.y + b.height)) <= tolerance;
        const insideX = world.x >= b.x - tolerance && world.x <= b.x + b.width + tolerance;
        const insideY = world.y >= b.y - tolerance && world.y <= b.y + b.height + tolerance;

        if (insideX && insideY) {
          if (nearTop && nearLeft) return { chart: entry.data, handle: 'nw' };
          if (nearTop && nearRight) return { chart: entry.data, handle: 'ne' };
          if (nearBottom && nearLeft) return { chart: entry.data, handle: 'sw' };
          if (nearBottom && nearRight) return { chart: entry.data, handle: 'se' };
          if (nearTop) return { chart: entry.data, handle: 'n' };
          if (nearBottom) return { chart: entry.data, handle: 's' };
          if (nearLeft) return { chart: entry.data, handle: 'w' };
          if (nearRight) return { chart: entry.data, handle: 'e' };
        }
      }

      if (intersects.rectanglePoint(b, world)) {
        return { chart: entry.data, handle: 'move' };
      }
    }
    return undefined;
  }

  /**
   * Hit tests a world position against the data-point hover regions of the
   * charts on the current sheet, returning the hovered region's tooltip
   * text. Regions are tested back-to-front so later-drawn elements win.
   */
  hitTestRegion(world: Point): ChartRegionHit | undefined {
    const sheetCharts = this.chartsPerSheet.get(sheets.current);
    if (!sheetCharts) return undefined;

    const entries = [...sheetCharts.values()].reverse();
    for (const entry of entries) {
      const b = entry.bounds;
      if (!intersects.rectanglePoint(b, world)) continue;

      // the regions were laid out for layoutSize; while a stretch preview is
      // showing (drag/resize before the reflow arrives) they don't match the
      // on-screen geometry, so skip hover entirely
      if (entry.layoutSize.w !== b.width || entry.layoutSize.h !== b.height) return undefined;

      const x = world.x - b.x;
      const y = world.y - b.y;
      for (let i = entry.hitRegions.length - 1; i >= 0; i--) {
        const region = entry.hitRegions[i];
        if (hitsShape(region.shape, x, y)) {
          return { chartId: entry.data.id, region, title: region.title, value: region.value ?? undefined };
        }
      }
      // the topmost chart under the pointer consumes the hover
      return undefined;
    }
    return undefined;
  }

  /**
   * Renders a chart's container to a base64 PNG data URL (used by the AI's
   * review_chart_image tool).
   */
  async captureChart(sheetId: string, id: string): Promise<string | undefined> {
    const entry = this.chartsPerSheet.get(sheetId)?.get(id);
    if (!entry) return undefined;
    const wasVisible = entry.container.visible;
    entry.container.visible = true;
    try {
      return await pixiApp.renderer.extract.base64(entry.container, 'image/png');
    } catch (e) {
      console.warn('Failed to capture chart image:', e);
      return undefined;
    } finally {
      entry.container.visible = wasVisible;
    }
  }

  /** The current pixel bounds of a chart on the given sheet, if it exists. */
  chartBounds(sheetId: string, id: string): Rectangle | undefined {
    return this.chartsPerSheet.get(sheetId)?.get(id)?.bounds;
  }

  /** The chart data for an id on the given sheet, if it exists. */
  chart(sheetId: string, id: string): JsChart | undefined {
    return this.chartsPerSheet.get(sheetId)?.get(id)?.data;
  }

  /** The chart (on the given sheet) whose pixel rect contains the point. */
  chartAtSheetPoint(sheetId: string, x: number, y: number): JsChart | undefined {
    const sheetCharts = this.chartsPerSheet.get(sheetId);
    if (!sheetCharts) return undefined;
    for (const entry of sheetCharts.values()) {
      if (
        x >= entry.data.x &&
        x <= entry.data.x + entry.data.w &&
        y >= entry.data.y &&
        y <= entry.data.y + entry.data.h
      ) {
        return entry.data;
      }
    }
    return undefined;
  }

  /**
   * Optimistically moves/resizes a chart during a drag. Stretches the current
   * contents as an instant preview; the reflowed display list follows via
   * `setTransientOps` (during the drag) and the authoritative layout from
   * core (on commit).
   */
  setTransientRect(id: string, rect: Rectangle) {
    const entry = this.chartsPerSheet.get(sheets.current)?.get(id);
    if (!entry) return;
    entry.bounds = rect.clone();
    entry.container.position.set(rect.x, rect.y);
    // scale relative to whatever size the current contents were laid out for
    if (entry.layoutSize.w > 0 && entry.layoutSize.h > 0) {
      entry.container.scale.set(rect.width / entry.layoutSize.w, rect.height / entry.layoutSize.h);
    }
    this.dirty = true;
  }

  /**
   * Replaces a chart's contents with a preview display list laid out for
   * `rect` (live reflow while resizing).
   */
  setTransientOps(id: string, rect: Rectangle, layout: JsChartLayout) {
    const entry = this.chartsPerSheet.get(sheets.current)?.get(id);
    if (!entry) return;
    entry.bounds = rect.clone();
    entry.layoutSize = { w: rect.width, h: rect.height };
    entry.hitRegions = layout.hit_regions ?? [];
    // the reflow replaced the regions; a highlighted element is now stale
    if (this.highlightedRegion?.chartId === id) {
      this.clearRegionHighlight();
    }
    entry.container.position.set(rect.x, rect.y);
    entry.container.scale.set(1, 1);
    drawDisplayList(entry.container, layout.ops, entry.data.images);
    this.dirty = true;
  }

  /**
   * Shows a live preview of the chart editor's in-progress edits. For a
   * chart that already exists on its sheet, its contents are temporarily
   * replaced; otherwise the preview draws as a ghost of the not-yet-created
   * chart.
   */
  showEditPreview(chart: JsChart) {
    const entry = this.chartsPerSheet.get(chart.sheet_id)?.get(chart.id);
    if (entry) {
      if (this.editPreviewGhost) {
        this.clearEditGhost();
      }
      this.editPreviewId = chart.id;
      this.setTransientOps(chart.id, new Rectangle(chart.x, chart.y, chart.w, chart.h), {
        ops: chart.ops,
        hit_regions: chart.hit_regions ?? [],
      });
      return;
    }

    if (this.editPreviewId) {
      this.restore(this.editPreviewId);
      this.editPreviewId = undefined;
    }
    if (!this.editPreviewGhost) {
      this.editPreviewGhost = {
        container: this.addChild(new Container()),
        bounds: new Rectangle(chart.x, chart.y, chart.w, chart.h),
      };
      // keep the selection graphics on top
      this.addChild(this.selection);
    }
    const ghost = this.editPreviewGhost;
    ghost.bounds = new Rectangle(chart.x, chart.y, chart.w, chart.h);
    ghost.container.position.set(chart.x, chart.y);
    ghost.container.alpha = 0.8;
    drawDisplayList(ghost.container, chart.ops, chart.images);
    this.dirty = true;
  }

  /** Removes any edit preview, restoring existing charts to their saved state. */
  clearEditPreview() {
    if (this.editPreviewId) {
      this.restore(this.editPreviewId);
      this.editPreviewId = undefined;
    }
    this.clearEditGhost();
  }

  private clearEditGhost() {
    if (this.editPreviewGhost) {
      this.editPreviewGhost.container.destroy({ children: true });
      this.editPreviewGhost = undefined;
      this.dirty = true;
    }
  }

  /**
   * Shows exactly the charts on `sheetId` that intersect `cull`, hiding the
   * selection outline and any edit preview. Used while rendering thumbnails
   * and image exports, where the target sheet/rect may differ from the live
   * viewport.
   */
  cullForCopying(sheetId: string, cull: Rectangle) {
    for (const [id, sheetCharts] of this.chartsPerSheet) {
      const isTargetSheet = id === sheetId;
      for (const entry of sheetCharts.values()) {
        entry.container.visible = isTargetSheet && intersects.rectangleRectangle(entry.bounds, cull);
      }
    }
    if (this.editPreviewGhost) {
      this.editPreviewGhost.container.visible = false;
    }
    this.selection.visible = false;
  }

  /** Restores chart visibility from the live viewport after copying. */
  restoreAfterCopying() {
    this.selection.visible = true;
    this.update();
  }

  /** Restores a chart to its last authoritative state (cancelled drag). */
  restore(id: string) {
    const entry = this.chartsPerSheet.get(sheets.current)?.get(id);
    if (!entry) return;
    const { data } = entry;
    entry.bounds = new Rectangle(data.x, data.y, data.w, data.h);
    entry.layoutSize = { w: data.w, h: data.h };
    entry.hitRegions = data.hit_regions ?? [];
    entry.container.position.set(data.x, data.y);
    entry.container.scale.set(1, 1);
    this.drawEntry(entry);
    this.dirty = true;
  }

  /** Draws other players' chart selections as outlines in each player's
   * color. Drawn here (not UIMultiplayerCursor) so they sit above the
   * charts' opaque backgrounds. */
  private drawMultiplayerSelections() {
    const sheetCharts = this.chartsPerSheet.get(sheets.current);
    if (!sheetCharts) return;
    const scale = pixiApp.viewport.scaled;
    multiplayer.users.forEach((player) => {
      if (player.sheet_id !== sheets.current) return;
      const chartId = player.parsedSelection?.getSelectedChartId();
      if (!chartId) return;
      const entry = sheetCharts.get(chartId);
      if (!entry) return;
      const b = entry.bounds;
      this.selection.lineStyle({ width: 2 / scale, color: player.color, alpha: 0.5 });
      this.selection.drawRect(b.x, b.y, b.width, b.height);
    });
  }

  private drawSelection() {
    this.selection.clear();
    this.drawMultiplayerSelections();
    const sheetCharts = this.chartsPerSheet.get(sheets.current);
    const entry = this.selectedId ? sheetCharts?.get(this.selectedId) : undefined;
    if (!entry) return;

    const b = entry.bounds;
    const scale = pixiApp.viewport.scaled;
    const accent = content.accentColor;

    this.selection.lineStyle({ width: 2 / scale, color: accent });
    this.selection.drawRect(b.x, b.y, b.width, b.height);

    const handleSize = CHART_RESIZE_HANDLE_SIZE / scale;
    this.selection.lineStyle({ width: 1 / scale, color: accent });
    this.selection.beginFill(0xffffff);
    for (const fx of [0, 0.5, 1]) {
      for (const fy of [0, 0.5, 1]) {
        if (fx === 0.5 && fy === 0.5) continue;
        this.selection.drawRect(
          b.x + b.width * fx - handleSize / 2,
          b.y + b.height * fy - handleSize / 2,
          handleSize,
          handleSize
        );
      }
    }
    this.selection.endFill();
  }

  /** Draws the accent outline over the clicked chart element. */
  private drawRegionHighlight() {
    const highlighted = this.highlightedRegion;
    if (!highlighted) return;
    const entry = this.chartsPerSheet.get(sheets.current)?.get(highlighted.chartId);
    if (!entry || !entry.container.visible) return;

    const { x: ox, y: oy } = entry.bounds;
    const scale = pixiApp.viewport.scaled;
    const shape = highlighted.region.shape;
    this.selection.lineStyle({ width: 2 / scale, color: content.accentColor });

    switch (shape.type) {
      case 'Rect':
        this.selection.drawRect(ox + shape.x, oy + shape.y, shape.w, shape.h);
        break;

      case 'Circle':
        this.selection.drawCircle(ox + shape.cx, oy + shape.cy, shape.radius);
        break;

      case 'PieSlice': {
        // display-list angles are clockwise from 12 o'clock; canvas angles
        // are clockwise (y-down) from 3 o'clock
        const start = shape.start_angle - Math.PI / 2;
        const end = shape.end_angle - Math.PI / 2;
        const cx = ox + shape.cx;
        const cy = oy + shape.cy;
        if (shape.inner_radius > 0) {
          this.selection.moveTo(cx + shape.inner_radius * Math.cos(start), cy + shape.inner_radius * Math.sin(start));
          this.selection.lineTo(cx + shape.radius * Math.cos(start), cy + shape.radius * Math.sin(start));
          this.selection.arc(cx, cy, shape.radius, start, end);
          this.selection.lineTo(cx + shape.inner_radius * Math.cos(end), cy + shape.inner_radius * Math.sin(end));
          this.selection.arc(cx, cy, shape.inner_radius, end, start, true);
          this.selection.closePath();
        } else {
          this.selection.moveTo(cx, cy);
          this.selection.arc(cx, cy, shape.radius, start, end);
          this.selection.closePath();
        }
        break;
      }

      case 'Polyline': {
        const points = shape.points;
        if (points.length >= 4) {
          this.selection.moveTo(ox + points[0], oy + points[1]);
          for (let j = 2; j + 1 < points.length; j += 2) {
            this.selection.lineTo(ox + points[j], oy + points[j + 1]);
          }
        }
        break;
      }

      case 'Polygon': {
        const points = shape.points;
        if (points.length >= 6) {
          this.selection.moveTo(ox + points[0], oy + points[1]);
          for (let j = 2; j + 1 < points.length; j += 2) {
            this.selection.lineTo(ox + points[j], oy + points[j + 1]);
          }
          this.selection.closePath();
        }
        break;
      }
    }
  }

  update() {
    const viewportBounds = pixiApp.viewport.getVisibleBounds();

    // A chart overlapping the frozen rows/columns is drawn by the pinned
    // frozen-pane passes, so it must stay renderable regardless of how far
    // the body has scrolled (see FrozenPanes).
    const sheet = sheets.sheet;
    const boundary = sheet?.getFrozenBoundary();
    const inFrozenBand = (bounds: Rectangle): boolean =>
      !!sheet && !!boundary && intersectsFrozenBand(bounds, sheet.frozenRows, sheet.frozenColumns, boundary);

    for (const [sheetId, sheetCharts] of this.chartsPerSheet) {
      const isCurrentSheet = sheetId === sheets.current;
      for (const entry of sheetCharts.values()) {
        entry.container.visible =
          isCurrentSheet && (intersects.rectangleRectangle(entry.bounds, viewportBounds) || inFrozenBand(entry.bounds));
      }
    }

    if (this.editPreviewGhost) {
      this.editPreviewGhost.container.visible =
        intersects.rectangleRectangle(this.editPreviewGhost.bounds, viewportBounds) ||
        inFrozenBand(this.editPreviewGhost.bounds);
    }

    this.drawSelection();
    this.drawRegionHighlight();
    this.dirty = false;
  }
}
